import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import { normalizeBusinessRules } from '../../services/businessRules.service'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import './AdminBusinessRules.css'

const createMethodTemplate = (type) => {
  const id = `${type}_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`
  if (type === 'cash') {
    return { id, type, label: 'Efectivo', enabled: true, customerNote: '', priceAdjustment: null, config: {} }
  }
  if (type === 'bank_transfer') {
    return { id, type, label: 'Transferencia', enabled: true, customerNote: '', priceAdjustment: null, config: { accounts: [] } }
  }
  if (type === 'postnet') {
    return { id, type, label: 'Tarjeta (Postnet)', enabled: true, customerNote: '', priceAdjustment: null, config: { programs: [] } }
  }
  if (type === 'gateway_mercadopago') {
    return { id, type, label: 'MercadoPago', enabled: true, customerNote: '', priceAdjustment: null, config: { publicKey: '', accessTokenRef: '' } }
  }
  return { id, type: 'custom', label: 'Medio de pago', enabled: true, customerNote: '', priceAdjustment: null, config: {} }
}

function AdminBusinessRules() {
  const { rules, loading, update } = useBusinessRulesContext()
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const [formData, setFormData] = useState(null)
  const [activeTab, setActiveTab] = useState('stock')
  const [selectedMethodId, setSelectedMethodId] = useState(null)
  const [selectedPostnetProgramIndex, setSelectedPostnetProgramIndex] = useState(0)
  const [brokenLogoKeys, setBrokenLogoKeys] = useState(() => new Set())

  useEffect(() => {
    if (rules) setFormData(rules)
  }, [rules])

  useEffect(() => {
    if (!formData) return
    const methods = Array.isArray(formData?.paymentMethods?.methods) ? formData.paymentMethods.methods : []
    if (!methods.length) {
      setSelectedMethodId(null)
      return
    }
    const exists = selectedMethodId && methods.some(m => m?.id === selectedMethodId)
    if (!exists) setSelectedMethodId(methods[0].id)
  }, [formData, selectedMethodId])

  useEffect(() => {
    if (!formData || !selectedMethodId) return
    const methods = Array.isArray(formData?.paymentMethods?.methods) ? formData.paymentMethods.methods : []
    const method = methods.find(m => m?.id === selectedMethodId)
    if (method?.type !== 'postnet') return
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    if (!programs.length) {
      setSelectedPostnetProgramIndex(0)
      return
    }
    if (selectedPostnetProgramIndex < 0 || selectedPostnetProgramIndex >= programs.length) {
      setSelectedPostnetProgramIndex(0)
    }
  }, [formData, selectedMethodId, selectedPostnetProgramIndex])

  const paymentMethods = useMemo(() => formData?.paymentMethods?.methods || [], [formData])

  const setNested = (path, value) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      let cursor = next
      for (let i = 0; i < path.length - 1; i++) {
        cursor[path[i]] = cursor[path[i]] ?? {}
        cursor = cursor[path[i]]
      }
      cursor[path[path.length - 1]] = value
      return next
    })
  }

  const updateMethod = (index, patch) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      const methods = Array.isArray(next.paymentMethods?.methods) ? next.paymentMethods.methods : []
      methods[index] = { ...methods[index], ...patch }
      next.paymentMethods = { ...(next.paymentMethods || {}), methods }
      return next
    })
  }

  const removeMethod = (index) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      next.paymentMethods = next.paymentMethods || { enabled: true, methods: [] }
      next.paymentMethods.methods = (next.paymentMethods.methods || []).filter((_, i) => i !== index)
      return next
    })
  }

  const moveMethod = (from, to) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      const methods = [...(next.paymentMethods?.methods || [])]
      const [item] = methods.splice(from, 1)
      methods.splice(to, 0, item)
      next.paymentMethods = { ...(next.paymentMethods || {}), methods }
      return next
    })
  }

  const addMethod = (type) => {
    const template = createMethodTemplate(type)
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      next.paymentMethods = next.paymentMethods || { enabled: true, methods: [] }
      next.paymentMethods.methods = [...(next.paymentMethods.methods || []), template]
      return next
    })
    setActiveTab('payments')
    setSelectedMethodId(template.id)
  }

  const setMethodConfig = (index, config) => {
    updateMethod(index, { config })
  }

  const setMethodAdjustment = (index, patch) => {
    const current = paymentMethods[index]?.priceAdjustment || null
    const next = { ...(current || { kind: 'percent', sign: 'discount', value: 0 }), ...patch }
    if (!Number(next.value)) {
      updateMethod(index, { priceAdjustment: null })
      return
    }
    updateMethod(index, { priceAdjustment: next })
  }

  const addTransferAccount = (methodIndex) => {
    const method = paymentMethods[methodIndex]
    const accounts = Array.isArray(method?.config?.accounts) ? method.config.accounts : []
    setMethodConfig(methodIndex, { ...(method.config || {}), accounts: [...accounts, { bankName: '', holder: '', cbu: '', alias: '', cuit: '', note: '' }] })
  }

  const updateTransferAccount = (methodIndex, accountIndex, patch) => {
    const method = paymentMethods[methodIndex]
    const accounts = Array.isArray(method?.config?.accounts) ? method.config.accounts : []
    const next = accounts.map((a, i) => (i === accountIndex ? { ...a, ...patch } : a))
    setMethodConfig(methodIndex, { ...(method.config || {}), accounts: next })
  }

  const removeTransferAccount = (methodIndex, accountIndex) => {
    const method = paymentMethods[methodIndex]
    const accounts = Array.isArray(method?.config?.accounts) ? method.config.accounts : []
    setMethodConfig(methodIndex, { ...(method.config || {}), accounts: accounts.filter((_, i) => i !== accountIndex) })
  }

  const addPostnetProgram = (methodIndex) => {
    const method = paymentMethods[methodIndex]
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    setMethodConfig(methodIndex, { ...(method.config || {}), programs: [...programs, { brand: '', logoUrl: '', installments: [{ count: 1, interestPercent: 0, note: '' }] }] })
  }

  const updatePostnetProgram = (methodIndex, programIndex, patch) => {
    const method = paymentMethods[methodIndex]
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    const next = programs.map((p, i) => (i === programIndex ? { ...p, ...patch } : p))
    setMethodConfig(methodIndex, { ...(method.config || {}), programs: next })
  }

  const removePostnetProgram = (methodIndex, programIndex) => {
    const method = paymentMethods[methodIndex]
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    setMethodConfig(methodIndex, { ...(method.config || {}), programs: programs.filter((_, i) => i !== programIndex) })
  }

  const addInstallment = (methodIndex, programIndex) => {
    const method = paymentMethods[methodIndex]
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    const current = programs[programIndex] || { installments: [] }
    const installments = Array.isArray(current.installments) ? current.installments : []
    const nextInstallments = [...installments, { count: 3, interestPercent: 0, note: '' }]
    const nextPrograms = programs.map((p, i) => (i === programIndex ? { ...p, installments: nextInstallments } : p))
    setMethodConfig(methodIndex, { ...(method.config || {}), programs: nextPrograms })
  }

  const updateInstallment = (methodIndex, programIndex, installmentIndex, patch) => {
    const method = paymentMethods[methodIndex]
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    const current = programs[programIndex] || { installments: [] }
    const installments = Array.isArray(current.installments) ? current.installments : []
    const nextInstallments = installments.map((it, i) => (i === installmentIndex ? { ...it, ...patch } : it))
    const nextPrograms = programs.map((p, i) => (i === programIndex ? { ...p, installments: nextInstallments } : p))
    setMethodConfig(methodIndex, { ...(method.config || {}), programs: nextPrograms })
  }

  const removeInstallment = (methodIndex, programIndex, installmentIndex) => {
    const method = paymentMethods[methodIndex]
    const programs = Array.isArray(method?.config?.programs) ? method.config.programs : []
    const current = programs[programIndex] || { installments: [] }
    const installments = Array.isArray(current.installments) ? current.installments : []
    const nextInstallments = installments.filter((_, i) => i !== installmentIndex)
    const nextPrograms = programs.map((p, i) => (i === programIndex ? { ...p, installments: nextInstallments.length ? nextInstallments : [{ count: 1, interestPercent: 0, note: '' }] } : p))
    setMethodConfig(methodIndex, { ...(method.config || {}), programs: nextPrograms })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData || saving) return
    setSaving(true)
    try {
      const normalized = normalizeBusinessRules(formData)
      await update(normalized)
      setFormData(normalized)
      setNotification({ type: 'success', message: 'Reglas de negocio actualizadas correctamente' })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error guardando reglas de negocio:', error)
      setNotification({ type: 'error', message: `Error al guardar: ${error.message || 'Error desconocido'}` })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !formData) {
    return (
      <div className="admin-business-rules-page">
        <div className="loading-state">
          <p>Cargando configuración...</p>
        </div>
      </div>
    )
  }

  const methodTypeLabel = (type) => {
    if (type === 'cash') return 'Efectivo'
    if (type === 'bank_transfer') return 'Transferencia'
    if (type === 'postnet') return 'Postnet'
    if (type === 'gateway_mercadopago') return 'MercadoPago'
    return 'Personalizado'
  }

  const modeLabel = (mode) => {
    if (mode === 'linked_dependent') return 'Vinculados (sincronización automática)'
    if (mode === 'linked_independent') return 'Vinculados (independientes)'
    if (mode === 'sales_only') return 'Solo órdenes de venta'
    return mode
  }

  const stockSummary = `${formData.outOfStockPolicy.visibility === 'hide' ? 'Ocultar' : 'Mostrar'} · ${
    formData.outOfStockPolicy.purchaseBehavior === 'allow_purchase'
      ? 'Permitir compra'
      : formData.outOfStockPolicy.purchaseBehavior === 'inquiry_only'
        ? 'Solo consulta'
        : 'Bloquear compra'
  }`

  const idSummary = (() => {
    const skuEnabled = formData.productIdentification?.skuAuto?.enabled === true
    const serialEnabled = formData.productIdentification?.serialAuto?.enabled === true
    const parts = Array.isArray(formData.productIdentification?.skuAuto?.parts) ? formData.productIdentification.skuAuto.parts : []
    const skuLabel = skuEnabled ? `SKU auto (${parts.length} partes)` : 'SKU manual'
    const serialLabel = serialEnabled ? 'Serie auto' : 'Serie manual'
    return `${skuLabel} · ${serialLabel}`
  })()

  const skuParts = Array.isArray(formData?.productIdentification?.skuAuto?.parts)
    ? formData.productIdentification.skuAuto.parts
    : []

  const updateSkuPart = (index, patch) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      const current = next.productIdentification?.skuAuto?.parts
      const parts = Array.isArray(current) ? [...current] : []
      const item = parts[index]
      if (!item) return prev
      parts[index] = { ...item, ...patch }
      next.productIdentification = next.productIdentification || {}
      next.productIdentification.skuAuto = next.productIdentification.skuAuto || { enabled: false, separator: '-', parts: [] }
      next.productIdentification.skuAuto.parts = parts
      return next
    })
  }

  const removeSkuPart = (index) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      const current = next.productIdentification?.skuAuto?.parts
      const parts = Array.isArray(current) ? current.filter((_, i) => i !== index) : []
      next.productIdentification = next.productIdentification || {}
      next.productIdentification.skuAuto = next.productIdentification.skuAuto || { enabled: false, separator: '-', parts: [] }
      next.productIdentification.skuAuto.parts = parts
      return next
    })
  }

  const moveSkuPart = (from, to) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      const current = next.productIdentification?.skuAuto?.parts
      const parts = Array.isArray(current) ? [...current] : []
      if (from < 0 || from >= parts.length || to < 0 || to >= parts.length) return prev
      const [item] = parts.splice(from, 1)
      parts.splice(to, 0, item)
      next.productIdentification = next.productIdentification || {}
      next.productIdentification.skuAuto = next.productIdentification.skuAuto || { enabled: false, separator: '-', parts: [] }
      next.productIdentification.skuAuto.parts = parts
      return next
    })
  }

  const addSkuPart = (field) => {
    if (!field) return
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      const current = next.productIdentification?.skuAuto?.parts
      const parts = Array.isArray(current) ? [...current] : []
      const take = field === 'counter' ? 4 : 3
      parts.push({ field, take })
      next.productIdentification = next.productIdentification || {}
      next.productIdentification.skuAuto = next.productIdentification.skuAuto || { enabled: false, separator: '-', parts: [] }
      next.productIdentification.skuAuto.parts = parts
      return next
    })
  }

  const selectedMethodIndex = selectedMethodId
    ? paymentMethods.findIndex(m => m?.id === selectedMethodId)
    : -1
  const selectedMethod = selectedMethodIndex >= 0 ? paymentMethods[selectedMethodIndex] : null
  const selectedMethodType = selectedMethod?.type || null
  const selectedPostnetPrograms = selectedMethodType === 'postnet'
    ? (Array.isArray(selectedMethod?.config?.programs) ? selectedMethod.config.programs : [])
    : []
  const selectedPostnetProgram = selectedPostnetPrograms[selectedPostnetProgramIndex] || null
  const selectedPostnetProgramKey = selectedMethodId ? `${selectedMethodId}:${selectedPostnetProgramIndex}` : null
  const selectedPostnetLogoIsBroken = selectedPostnetProgramKey ? brokenLogoKeys.has(selectedPostnetProgramKey) : false

  const changeMethodType = (index, nextType) => {
    setFormData(prev => {
      if (!prev) return prev
      const next = structuredClone(prev)
      const methods = Array.isArray(next.paymentMethods?.methods) ? next.paymentMethods.methods : []
      const current = methods[index]
      if (!current) return prev
      const template = createMethodTemplate(nextType)
      methods[index] = {
        ...template,
        id: current.id,
        enabled: current.enabled,
        label: current.label || template.label,
        customerNote: current.customerNote || ''
      }
      next.paymentMethods = { ...(next.paymentMethods || {}), methods }
      return next
    })
  }

  return (
    <div className="admin-business-rules-page">
      {notification && <Toast type={notification.type} message={notification.message} />}

      <form onSubmit={handleSubmit} className="business-rules-form">
        <div className="business-rules-header">
          <div className="business-rules-header-left">
            <h2>Reglas de Negocio</h2>
            <p className="business-rules-description">Configura reglas comerciales y medios de pago visibles en el storefront</p>
          </div>
          <div className="business-rules-header-right">
            <Link to="/admin/configuraciones" className="back-link">← Volver</Link>
            <Button type="submit" size="small" disabled={saving} className="br-save-top">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>

        <div className="br-layout">
          <aside className="br-nav">
            <button
              type="button"
              className={`br-nav-item ${activeTab === 'stock' ? 'active' : ''}`}
              onClick={() => setActiveTab('stock')}
              title={`Stock: ${stockSummary}`}
            >
              <div className="br-nav-title">Stock</div>
              <div className="br-nav-subtitle">{stockSummary}</div>
            </button>
            <button
              type="button"
              className={`br-nav-item ${activeTab === 'identification' ? 'active' : ''}`}
              onClick={() => setActiveTab('identification')}
              title={`Identificación: ${idSummary}`}
            >
              <div className="br-nav-title">Identificación</div>
              <div className="br-nav-subtitle">{idSummary}</div>
            </button>
            <button
              type="button"
              className={`br-nav-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
              title={`Pedidos y Ventas: ${modeLabel(formData.ordersSalesMode.mode)}`}
            >
              <div className="br-nav-title">Pedidos y Ventas</div>
              <div className="br-nav-subtitle">{modeLabel(formData.ordersSalesMode.mode)}</div>
            </button>
            <button
              type="button"
              className={`br-nav-item ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveTab('payments')}
              title={`Medios de Pago: ${formData.paymentMethods.enabled !== false ? `${paymentMethods.length} configurados` : 'Deshabilitados'}`}
            >
              <div className="br-nav-title">Medios de Pago</div>
              <div className="br-nav-subtitle">
                {formData.paymentMethods.enabled !== false ? `${paymentMethods.length} configurados` : 'Deshabilitados'}
              </div>
            </button>
          </aside>

          <section className="br-panel">
            {activeTab === 'stock' ? (
              <div className="br-panel-inner">
                <div className="br-panel-title">Stock</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Productos sin stock</label>
                    <select
                      value={formData.outOfStockPolicy.visibility}
                      onChange={(e) => setNested(['outOfStockPolicy', 'visibility'], e.target.value)}
                    >
                      <option value="show">Mostrar</option>
                      <option value="hide">Ocultar</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Acción al no haber stock</label>
                    <select
                      value={formData.outOfStockPolicy.purchaseBehavior}
                      onChange={(e) => setNested(['outOfStockPolicy', 'purchaseBehavior'], e.target.value)}
                    >
                      <option value="block_purchase">Bloquear compra</option>
                      <option value="allow_purchase">Permitir compra (por encargo)</option>
                      <option value="inquiry_only">Solo consulta</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Canal de consulta</label>
                    <select
                      value={formData.outOfStockPolicy.inquiryChannel}
                      onChange={(e) => setNested(['outOfStockPolicy', 'inquiryChannel'], e.target.value)}
                    >
                      <option value="whatsapp">WhatsApp</option>
                      <option value="help_page">Página de ayuda</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Texto sin stock</label>
                    <input
                      type="text"
                      value={formData.outOfStockPolicy.labelText || ''}
                      onChange={(e) => setNested(['outOfStockPolicy', 'labelText'], e.target.value)}
                      placeholder="Sin stock"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === 'identification' ? (
              <div className="br-panel-inner br-identification">
                <div className="br-panel-title">Identificación</div>

                <div className="br-identification-grid">
                  <div className="br-detail">
                    <div className="br-detail-header">
                      <div className="br-detail-title">SKU automático</div>
                      <div className="br-detail-subtitle">Arma el SKU desde partes configurables y separador.</div>
                    </div>

                    <div className="br-detail-grid">
                      <div className="form-group">
                        <label className="inline-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.productIdentification?.skuAuto?.enabled === true}
                            onChange={(e) => setNested(['productIdentification', 'skuAuto', 'enabled'], e.target.checked)}
                          />
                          Habilitar
                        </label>
                      </div>

                      <div className="form-group">
                        <label>Separador</label>
                        <input
                          type="text"
                          value={formData.productIdentification?.skuAuto?.separator || '-'}
                          onChange={(e) => setNested(['productIdentification', 'skuAuto', 'separator'], e.target.value)}
                          placeholder="-"
                        />
                      </div>

                      <div className="form-group">
                        <label>Agregar parte</label>
                        <select
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) addSkuPart(e.target.value)
                            e.target.value = ''
                          }}
                        >
                          <option value="" disabled>Seleccionar...</option>
                          <option value="name">Nombre</option>
                          <option value="brand">Marca</option>
                          <option value="collection">Colección</option>
                          <option value="color">Color</option>
                          <option value="size">Talle</option>
                          <option value="counter">Autonumérico</option>
                        </select>
                      </div>
                    </div>

                    <div className="br-parts">
                      {skuParts.length === 0 ? (
                        <div className="empty-box">Agregá al menos una parte para el SKU.</div>
                      ) : (
                        <div className="br-parts-list">
                          {skuParts.map((p, idx) => (
                            <div key={`${p.field}-${idx}`} className="br-part-row">
                              <div className="form-group">
                                <label>Campo</label>
                                <select
                                  value={p.field || 'name'}
                                  onChange={(e) => updateSkuPart(idx, { field: e.target.value })}
                                >
                                  <option value="name">Nombre</option>
                                  <option value="brand">Marca</option>
                                  <option value="collection">Colección</option>
                                  <option value="color">Color</option>
                                  <option value="size">Talle</option>
                                  <option value="counter">Autonumérico</option>
                                </select>
                              </div>

                              <div className="form-group">
                                <label>{p.field === 'counter' ? 'Dígitos' : 'Caracteres'}</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="30"
                                  value={p.take || 1}
                                  onChange={(e) => updateSkuPart(idx, { take: e.target.value })}
                                />
                              </div>

                              <div className="br-part-actions">
                                <button type="button" className="br-mini-btn" onClick={() => moveSkuPart(idx, idx - 1)} disabled={idx === 0} title="Subir">↑</button>
                                <button type="button" className="br-mini-btn" onClick={() => moveSkuPart(idx, idx + 1)} disabled={idx === skuParts.length - 1} title="Bajar">↓</button>
                                <button type="button" className="br-mini-btn danger" onClick={() => removeSkuPart(idx)} title="Eliminar">✕</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="br-detail">
                    <div className="br-detail-header">
                      <div className="br-detail-title">Número de serie</div>
                      <div className="br-detail-subtitle">Genera una serie secuencial sin repetirse.</div>
                    </div>

                    <div className="br-detail-grid">
                      <div className="form-group">
                        <label className="inline-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.productIdentification?.serialAuto?.enabled === true}
                            onChange={(e) => setNested(['productIdentification', 'serialAuto', 'enabled'], e.target.checked)}
                          />
                          Habilitar
                        </label>
                      </div>

                      <div className="form-group">
                        <label>Dígitos</label>
                        <input
                          type="number"
                          min="4"
                          max="30"
                          value={formData.productIdentification?.serialAuto?.digits ?? 15}
                          onChange={(e) => setNested(['productIdentification', 'serialAuto', 'digits'], e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === 'orders' ? (
              <div className="br-panel-inner">
                <div className="br-panel-title">Pedidos y Ventas</div>
                <div className="form-group">
                  <label>Modo de trabajo</label>
                  <select
                    value={formData.ordersSalesMode.mode}
                    onChange={(e) => setNested(['ordersSalesMode', 'mode'], e.target.value)}
                  >
                    <option value="linked_dependent">Vinculados (sincronización automática)</option>
                    <option value="linked_independent">Vinculados (independientes)</option>
                    <option value="sales_only">Solo órdenes de venta</option>
                  </select>
                  <p className="hint">“Solo órdenes de venta” finaliza el pedido automáticamente al completar el cobro.</p>
                </div>
              </div>
            ) : null}

            {activeTab === 'payments' ? (
              <div className="br-panel-inner">
                <div className="br-panel-title">Medios de Pago</div>

                <div className="br-payments-top">
                  <label className="inline-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.paymentMethods.enabled !== false}
                      onChange={(e) => setNested(['paymentMethods', 'enabled'], e.target.checked)}
                    />
                    Habilitados en checkout
                  </label>

                  <div className="payment-methods-toolbar">
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) addMethod(e.target.value)
                        e.target.value = ''
                      }}
                      disabled={formData.paymentMethods.enabled === false}
                    >
                      <option value="" disabled>Agregar medio...</option>
                      <option value="cash">Efectivo</option>
                      <option value="bank_transfer">Transferencia</option>
                      <option value="postnet">Postnet</option>
                      <option value="gateway_mercadopago">MercadoPago</option>
                      <option value="custom">Personalizado</option>
                    </select>
                  </div>
                </div>

                {paymentMethods.length === 0 ? (
                  <div className="empty-box">No hay medios de pago configurados.</div>
                ) : (
                  <div className="br-payments-layout">
                    <div className="br-list">
                      {paymentMethods.map((method, idx) => {
                        const isSelected = method.id === selectedMethodId
                        return (
                          <div key={method.id} className={`br-list-item ${isSelected ? 'selected' : ''}`}>
                            <button
                              type="button"
                              className="br-list-main"
                              onClick={() => {
                                setSelectedMethodId(method.id)
                                if (method.type === 'postnet') setSelectedPostnetProgramIndex(0)
                              }}
                              title={`${method.label || 'Sin nombre'} · ${methodTypeLabel(method.type)}`}
                            >
                              <input
                                type="checkbox"
                                checked={method.enabled !== false}
                                onChange={(e) => updateMethod(idx, { enabled: e.target.checked })}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="br-list-text">
                                <div className="br-list-title" title={method.label || 'Sin nombre'}>{method.label || 'Sin nombre'}</div>
                                <div className="br-list-subtitle">{methodTypeLabel(method.type)}</div>
                              </div>
                            </button>

                            <div className="br-list-actions">
                              <Button
                                type="button"
                                variant="secondary"
                                size="small"
                                className="br-btn"
                                onClick={() => moveMethod(idx, Math.max(0, idx - 1))}
                                disabled={idx === 0}
                              >
                                ↑
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="small"
                                className="br-btn"
                                onClick={() => moveMethod(idx, Math.min(paymentMethods.length - 1, idx + 1))}
                                disabled={idx === paymentMethods.length - 1}
                              >
                                ↓
                              </Button>
                              <Button
                                type="button"
                                variant="danger"
                                size="small"
                                className="br-btn"
                                onClick={() => removeMethod(idx)}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div className="br-detail">
                      {!selectedMethod ? (
                        <div className="empty-box">Seleccioná un medio de pago para editarlo.</div>
                      ) : (
                        <>
                          <div className="br-detail-header">
                            <div className="br-detail-title">Editar medio</div>
                            <div className="br-detail-subtitle">{methodTypeLabel(selectedMethod.type)}</div>
                          </div>

                          <div className="br-detail-grid">
                            <div className="form-group">
                              <label>Nombre visible</label>
                              <input
                                type="text"
                                value={selectedMethod.label || ''}
                                onChange={(e) => updateMethod(selectedMethodIndex, { label: e.target.value })}
                                placeholder="Ej: Transferencia bancaria"
                              />
                            </div>

                            <div className="form-group">
                              <label>Tipo</label>
                              <select
                                value={selectedMethod.type}
                                onChange={(e) => changeMethodType(selectedMethodIndex, e.target.value)}
                              >
                                <option value="cash">Efectivo</option>
                                <option value="bank_transfer">Transferencia</option>
                                <option value="postnet">Postnet</option>
                                <option value="gateway_mercadopago">MercadoPago</option>
                                <option value="custom">Personalizado</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Observación para el cliente</label>
                            <textarea
                              value={selectedMethod.customerNote || ''}
                              onChange={(e) => updateMethod(selectedMethodIndex, { customerNote: e.target.value })}
                              placeholder="Texto que verá el cliente al elegir este medio de pago"
                              rows={2}
                            />
                          </div>

                          {selectedMethod.type === 'postnet' ? (
                            <p className="hint">En Postnet, el interés/recargo o descuento se configura por cuota dentro de cada tarjeta.</p>
                          ) : (
                            <div className="adjustment-grid">
                              <div className="form-group">
                                <label>Ajuste</label>
                                <select
                                  value={selectedMethod.priceAdjustment?.sign || 'discount'}
                                  onChange={(e) => setMethodAdjustment(selectedMethodIndex, { sign: e.target.value })}
                                >
                                  <option value="discount">Descuento</option>
                                  <option value="surcharge">Recargo</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label>Tipo</label>
                                <select
                                  value={selectedMethod.priceAdjustment?.kind || 'percent'}
                                  onChange={(e) => setMethodAdjustment(selectedMethodIndex, { kind: e.target.value })}
                                >
                                  <option value="percent">%</option>
                                  <option value="fixed">$</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label>Valor</label>
                                <input
                                  type="number"
                                  value={selectedMethod.priceAdjustment?.value ?? 0}
                                  onChange={(e) => setMethodAdjustment(selectedMethodIndex, { value: Number(e.target.value) })}
                                  min="0"
                                  step="0.01"
                                />
                                <p className="hint">Si es 0, no se aplica ajuste.</p>
                              </div>
                            </div>
                          )}

                          {selectedMethod.type === 'bank_transfer' ? (
                            <div className="type-block">
                              <div className="type-block-header">
                                <h4>Datos bancarios</h4>
                                <Button type="button" variant="secondary" size="small" className="br-btn" onClick={() => addTransferAccount(selectedMethodIndex)}>
                                  Agregar cuenta
                                </Button>
                              </div>

                              {(selectedMethod.config?.accounts || []).length === 0 ? (
                                <div className="empty-box">No hay cuentas cargadas.</div>
                              ) : (
                                <div className="accounts-list">
                                  {(selectedMethod.config?.accounts || []).map((acc, accIdx) => (
                                    <div key={`${selectedMethod.id}:acc:${accIdx}`} className="account-card">
                                      <div className="account-grid">
                                        <input
                                          type="text"
                                          value={acc.bankName || ''}
                                          onChange={(e) => updateTransferAccount(selectedMethodIndex, accIdx, { bankName: e.target.value })}
                                          placeholder="Banco"
                                        />
                                        <input
                                          type="text"
                                          value={acc.holder || ''}
                                          onChange={(e) => updateTransferAccount(selectedMethodIndex, accIdx, { holder: e.target.value })}
                                          placeholder="Titular"
                                        />
                                        <input
                                          type="text"
                                          value={acc.cbu || ''}
                                          onChange={(e) => updateTransferAccount(selectedMethodIndex, accIdx, { cbu: e.target.value })}
                                          placeholder="CBU"
                                        />
                                        <input
                                          type="text"
                                          value={acc.alias || ''}
                                          onChange={(e) => updateTransferAccount(selectedMethodIndex, accIdx, { alias: e.target.value })}
                                          placeholder="Alias"
                                        />
                                        <input
                                          type="text"
                                          value={acc.cuit || ''}
                                          onChange={(e) => updateTransferAccount(selectedMethodIndex, accIdx, { cuit: e.target.value })}
                                          placeholder="CUIT"
                                        />
                                        <input
                                          type="text"
                                          value={acc.note || ''}
                                          onChange={(e) => updateTransferAccount(selectedMethodIndex, accIdx, { note: e.target.value })}
                                          placeholder="Nota"
                                        />
                                      </div>
                                      <div className="account-actions">
                                        <Button type="button" variant="danger" size="small" className="br-btn" onClick={() => removeTransferAccount(selectedMethodIndex, accIdx)}>
                                          Eliminar cuenta
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : null}

                          {selectedMethod.type === 'postnet' ? (
                            <div className="type-block br-postnet-block">
                              <div className="type-block-header">
                                <div className="br-postnet-header-text">
                                  <h4>Tarjetas (Postnet)</h4>
                                  <p className="hint">El interés/recargo o descuento se configura por cuota dentro de cada tarjeta.</p>
                                </div>
                                <Button type="button" variant="secondary" size="small" className="br-btn" onClick={() => addPostnetProgram(selectedMethodIndex)}>
                                  Agregar tarjeta
                                </Button>
                              </div>

                              {selectedPostnetPrograms.length === 0 ? (
                                <div className="empty-box">No hay tarjetas cargadas.</div>
                              ) : (
                                <>
                                  <div className="br-postnet-tabs" role="tablist" aria-label="Tarjetas Postnet">
                                    {selectedPostnetPrograms.map((p, pIdx) => (
                                      <button
                                        key={`${selectedMethod.id}:p:${pIdx}`}
                                        type="button"
                                        role="tab"
                                        aria-selected={pIdx === selectedPostnetProgramIndex}
                                        className={`br-postnet-tab ${pIdx === selectedPostnetProgramIndex ? 'selected' : ''}`}
                                        onClick={() => setSelectedPostnetProgramIndex(pIdx)}
                                        title={p.brand || 'Sin marca'}
                                      >
                                        <div className="br-postnet-tab-title">{p.brand || 'Sin marca'}</div>
                                        <div className="br-postnet-tab-subtitle">{(p.installments || []).length} cuotas</div>
                                      </button>
                                    ))}
                                  </div>

                                  {selectedPostnetProgram ? (
                                    <div className="br-postnet-editor">
                                      <div className="br-postnet-editor-top">
                                        <div className="form-group">
                                          <label>Marca</label>
                                          <input
                                            type="text"
                                            value={selectedPostnetProgram.brand || ''}
                                            onChange={(e) => updatePostnetProgram(selectedMethodIndex, selectedPostnetProgramIndex, { brand: e.target.value })}
                                            placeholder="Ej: MasterCard"
                                          />
                                        </div>
                                        <div className="br-postnet-editor-actions">
                                          <Button
                                            type="button"
                                            variant="danger"
                                            size="small"
                                            className="br-btn"
                                            onClick={() => {
                                              removePostnetProgram(selectedMethodIndex, selectedPostnetProgramIndex)
                                              setSelectedPostnetProgramIndex(0)
                                            }}
                                          >
                                            Eliminar tarjeta
                                          </Button>
                                        </div>
                                      </div>

                                      <div className="form-group">
                                        <label>Logo (opcional)</label>
                                        <div className="br-logo-uploader">
                                          <div className="program-logo">
                                            <input
                                              type="url"
                                              value={selectedPostnetProgram.logoUrl || ''}
                                              onChange={(e) => {
                                                if (selectedPostnetProgramKey) {
                                                  setBrokenLogoKeys(prev => {
                                                    const next = new Set(prev)
                                                    next.delete(selectedPostnetProgramKey)
                                                    return next
                                                  })
                                                }
                                                updatePostnetProgram(selectedMethodIndex, selectedPostnetProgramIndex, { logoUrl: e.target.value })
                                              }}
                                              placeholder="https://... (URL del logo)"
                                            />
                                            {selectedPostnetProgram.logoUrl && !selectedPostnetLogoIsBroken ? (
                                              <img
                                                src={selectedPostnetProgram.logoUrl}
                                                alt={selectedPostnetProgram.brand || 'logo'}
                                                onError={() => {
                                                  if (!selectedPostnetProgramKey) return
                                                  setBrokenLogoKeys(prev => new Set(prev).add(selectedPostnetProgramKey))
                                                }}
                                              />
                                            ) : (
                                              <div className="logo-placeholder">Sin logo</div>
                                            )}
                                          </div>
                                          {selectedPostnetProgram.logoUrl && selectedPostnetLogoIsBroken ? (
                                            <div className="logo-error">No se pudo cargar el logo con esa URL.</div>
                                          ) : null}
                                        </div>
                                      </div>

                                      <div className="installments-header">
                                        <h5>Cuotas</h5>
                                        <Button type="button" variant="secondary" size="small" className="br-btn" onClick={() => addInstallment(selectedMethodIndex, selectedPostnetProgramIndex)}>
                                          Agregar cuota
                                        </Button>
                                      </div>

                                      <div className="br-installment-list">
                                        {(selectedPostnetProgram.installments || []).map((it, itIdx) => (
                                          <div key={`${selectedMethod.id}:p:${selectedPostnetProgramIndex}:i:${itIdx}`} className="br-installment-card">
                                            <div className="br-installment-fields">
                                              <div className="form-group">
                                                <label>Cuotas</label>
                                                <input
                                                  type="number"
                                                  min="1"
                                                  step="1"
                                                  value={it.count ?? 1}
                                                  onChange={(e) => updateInstallment(selectedMethodIndex, selectedPostnetProgramIndex, itIdx, { count: Number(e.target.value) })}
                                                />
                                              </div>
                                              <div className="form-group">
                                                <label>% interés</label>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  value={it.interestPercent ?? 0}
                                                  onChange={(e) => updateInstallment(selectedMethodIndex, selectedPostnetProgramIndex, itIdx, { interestPercent: Number(e.target.value) })}
                                                />
                                              </div>
                                              <div className="form-group br-installment-note">
                                                <label>Nota</label>
                                                <input
                                                  type="text"
                                                  value={it.note || ''}
                                                  onChange={(e) => updateInstallment(selectedMethodIndex, selectedPostnetProgramIndex, itIdx, { note: e.target.value })}
                                                  placeholder="Opcional"
                                                />
                                              </div>
                                              <div className="br-installment-remove">
                                                <Button type="button" variant="danger" size="small" className="br-btn" onClick={() => removeInstallment(selectedMethodIndex, selectedPostnetProgramIndex, itIdx)}>
                                                  Quitar
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </div>
                          ) : null}

                          {selectedMethod.type === 'gateway_mercadopago' ? (
                            <div className="type-block">
                              <h4>MercadoPago</h4>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Public Key</label>
                                  <input
                                    type="text"
                                    value={selectedMethod.config?.publicKey || ''}
                                    onChange={(e) => setMethodConfig(selectedMethodIndex, { ...(selectedMethod.config || {}), publicKey: e.target.value })}
                                    placeholder="APP_USR-..."
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Referencia del Access Token</label>
                                  <input
                                    type="text"
                                    value={selectedMethod.config?.accessTokenRef || ''}
                                    onChange={(e) => setMethodConfig(selectedMethodIndex, { ...(selectedMethod.config || {}), accessTokenRef: e.target.value })}
                                    placeholder="mp_access_token"
                                  />
                                  <p className="hint">Se usa del lado servidor para generar el link de pago.</p>
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>

        <div className="form-actions">
          <Button type="submit" disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default AdminBusinessRules
